import { useState, useEffect, useRef, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, buildSrcSet, getImageSizes } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function CarouselSection({ block = {}, mediaFolders = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const variant = config.variant || 'carousel';
    const _allImages = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
    const _sel = content.selected_file_ids;
    const folderImages = _sel?.length > 0 ? _allImages.filter(f => _sel.includes(f.id)) : _allImages;
    const items = folderImages;
    const showTitle = config.show_title !== false;
    const showSub = !!config.show_subtitle;
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [sectionRef, visible] = useScrollReveal(0.15, !enableAnimations);
    const touchStart = useRef(null);
    const anim = getAnimation(config, visible);

    const next = useCallback(() => setCurrent(p => (p + 1) % items.length), [items.length]);
    const prev = useCallback(() => setCurrent(p => (p - 1 + items.length) % items.length), [items.length]);

    useEffect(() => {
        if ((variant !== 'carousel' && variant !== 'fullscreen' && variant !== 'coverflow') || items.length <= 1 || paused) return;
        const timer = setInterval(next, 5000);
        return () => clearInterval(timer);
    }, [variant, items.length, paused, next]);

    if (items.length === 0) return (
        <section className={`${s.padClass} relative`} style={s.bgStyle}>
            <div className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                <div className="w-full aspect-video rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                    <svg className="w-14 h-14 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="2" y="2" width="20" height="20" rx="2" strokeWidth={1.5}/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2 15l5-5 3 3 4-4 8 8"/><circle cx="15.5" cy="7.5" r="1.5" strokeWidth={1.5}/></svg>
                    <p className="text-sm text-gray-400 font-medium">Selecciona una carpeta de imagenes</p>
                </div>
            </div>
        </section>
    );

    const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        if (touchStart.current === null) return;
        const diff = touchStart.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
        touchStart.current = null;
    };

    const Header = () => (content.tag || content.title) ? <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/> : null;
    const Arrows = ({cls = ''}) => items.length > 1 && (
        <>
            <button onClick={prev} className={`absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all hover:scale-110 ${cls}`}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
            </button>
            <button onClick={next} className={`absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all hover:scale-110 ${cls}`}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
            </button>
        </>
    );
    const Dots = () => items.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
            {items.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all ${i === current ? 'w-8 bg-white' : 'w-2 bg-white/40'}`}/>)}
        </div>
    );
    const RingProgress = () => {
        if (items.length <= 1) return null;
        const size = 48, stroke = 3, radius = (size - stroke) / 2;
        const circumference = 2 * Math.PI * radius;
        return (
            <div className="flex items-center justify-center gap-3 mt-4">
                <button onClick={prev} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                </button>
                <div className="relative flex items-center justify-center" style={{width: size, height: size}}>
                    <svg className="absolute -rotate-90" width={size} height={size}>
                        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke}/>
                        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="white" strokeWidth={stroke}
                            strokeDasharray={circumference}
                            strokeDashoffset={circumference - (circumference * (current + 1)) / items.length}
                            strokeLinecap="round"
                            className="transition-all duration-500"/>
                    </svg>
                    <span className="text-white text-xs font-medium">{current + 1}/{items.length}</span>
                </div>
                <button onClick={next} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                </button>
            </div>
        );
    };

    // ── Lightbox overlay ──
    const LightboxOverlay = () => lightbox !== null && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
            <button onClick={e => {e.stopPropagation(); setLightbox(p => (p-1+items.length)%items.length);}} className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
            </button>
            <img src={items[lightbox]?.image_url} srcSet={buildSrcSet(items[lightbox])} sizes="90vw" alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" decoding="async" onClick={e => e.stopPropagation()}/>
            <button onClick={e => {e.stopPropagation(); setLightbox(p => (p+1)%items.length);}} className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
            </button>
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">{lightbox + 1} / {items.length}</div>
        </div>
    );

    // ── Grid ──
    if (variant === 'grid') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {items.map((img, i) => (
                            <div key={img.id || i} className={`group rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{transitionDelay: visible ? `${i*60}ms` : '0ms'}}>
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(4)} alt={img.title||''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" decoding="async"/>
                                </div>
                                {showTitle && img.title && <div className="p-3" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff' }}><p className="text-sm font-medium truncate" style={{color:s.text}}>{img.title}</p>{showSub && img.subtitle && <p className="text-xs truncate" style={{ color: s.textSub }}>{img.subtitle}</p>}</div>}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Mosaic (1 big + 2 small alternating) ──
    if (variant === 'mosaic') {
        const mCols = config.mosaic_columns || 3;
        const mAspect = config.mosaic_aspect || '4/3';
        const mGap = config.mosaic_gap || 'normal';
        const mLayout = config.mosaic_layout || 'uniform';
        const mHover = config.mosaic_hover || 'zoom';
        const mBorder = config.mosaic_border || 'shadow';
        const mRadius = config.mosaic_radius || 'medium';
        const gapPx = mGap === 'none' ? 0 : mGap === 'small' ? 8 : mGap === 'large' ? 24 : 16;
        const radiusClass = mRadius === 'none' ? 'rounded-none' : mRadius === 'small' ? 'rounded-lg' : mRadius === 'large' ? 'rounded-3xl' : 'rounded-xl';
        const borderClass = mBorder === 'none' ? '' : mBorder === 'border' ? 'border border-gray-200' : mBorder === 'shadow' ? 'shadow-sm hover:shadow-xl' : '';

        // Hover overlay
        const HoverOverlay = ({img}) => {
            if (!showTitle || !img.title) return null;
            if (mHover === 'none') return null;
            if (mHover === 'slide-up') return (
                <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-black/70 p-4">
                    <p className="text-white font-semibold text-sm">{img.title}</p>
                    {showSub && img.subtitle && <p className="text-white/70 text-xs mt-1">{img.subtitle}</p>}
                </div>
            );
            if (mHover === 'blur') return (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-sm bg-black/30 flex items-center justify-center p-4">
                    <div className="text-center"><p className="text-white font-bold text-base">{img.title}</p>
                    {showSub && img.subtitle && <p className="text-white/70 text-xs mt-1">{img.subtitle}</p>}</div>
                </div>
            );
            if (mHover === 'darken') return (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-end p-4">
                    <div className="translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                        <p className="text-white font-semibold text-sm">{img.title}</p>
                        {showSub && img.subtitle && <p className="text-white/70 text-xs mt-1">{img.subtitle}</p>}
                    </div>
                </div>
            );
            // default: zoom (gradient overlay)
            return (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <div><p className="text-white font-semibold text-sm">{img.title}</p>{showSub && img.subtitle && <p className="text-white/70 text-xs">{img.subtitle}</p>}</div>
                </div>
            );
        };
        const imgHoverClass = mHover === 'zoom' ? 'group-hover:scale-110' : mHover === 'none' ? '' : '';

        // ── Masonry layout ──
        if (mLayout === 'masonry' || mLayout === 'pinterest') {
            const colCount = mCols;
            const columns = Array.from({length: colCount}, () => []);
            items.forEach((img, i) => columns[i % colCount].push({img, i}));
            return (
                <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                    <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                    <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                        <Header/>
                        <div className="flex" style={{gap: gapPx}}>
                            {columns.map((col, ci) => (
                                <div key={ci} className="flex-1 flex flex-col" style={{gap: gapPx}}>
                                    {col.map(({img, i}) => (
                                        <div key={img.id || i}
                                            className={`relative group overflow-hidden ${radiusClass} ${borderClass} cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                            style={{transitionDelay: visible ? `${i * 60}ms` : '0ms'}}
                                            onClick={() => setLightbox(i)}>
                                            <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(mCols)} alt={img.title||''} className={`w-full h-auto object-cover transition-transform duration-700 ${imgHoverClass}`} loading="lazy" decoding="async"/>
                                            <HoverOverlay img={img}/>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                    <LightboxOverlay/>
                </section>
            );
        }

        // ── Highlighted layout (1 big + small) ──
        if (mLayout === 'highlighted') {
            const groups = [];
            for (let i = 0; i < items.length; i += mCols) groups.push(items.slice(i, i + mCols));
            return (
                <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                    <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                    <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                        <Header/>
                        <div className="flex flex-col" style={{gap: gapPx}}>
                            {groups.map((group, gi) => {
                                const big = group[0];
                                const smalls = group.slice(1);
                                const flip = gi % 2 === 1;
                                return (
                                    <div key={gi} className="grid grid-cols-1 md:grid-cols-2" style={{gap: gapPx}}>
                                        {/* Big image */}
                                        <div className={`${flip ? 'md:order-2' : ''} relative group overflow-hidden ${radiusClass} ${borderClass} cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                            style={{transitionDelay: visible ? `${gi * 3 * 60}ms` : '0ms'}}
                                            onClick={() => setLightbox(gi * mCols)}>
                                            <div className="overflow-hidden h-full" style={{aspectRatio: mAspect}}>
                                                <img src={big.image_url} srcSet={buildSrcSet(big)} sizes={getImageSizes(2)} alt={big.title||''} className={`w-full h-full object-cover transition-transform duration-700 ${imgHoverClass}`} loading="lazy" decoding="async"/>
                                            </div>
                                            <HoverOverlay img={big}/>
                                        </div>
                                        {/* Small images stacked */}
                                        <div className={`${flip ? 'md:order-1' : ''} flex flex-col`} style={{gap: gapPx}}>
                                            {smalls.map((img, si) => (
                                                <div key={img.id || si} className={`relative group overflow-hidden ${radiusClass} ${borderClass} cursor-pointer flex-1 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                                    style={{transitionDelay: visible ? `${(gi * mCols + si + 1) * 60}ms` : '0ms'}}
                                                    onClick={() => setLightbox(gi * mCols + si + 1)}>
                                                    <div className="overflow-hidden h-full">
                                                        <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(2)} alt={img.title||''} className={`w-full h-full object-cover transition-transform duration-700 ${imgHoverClass}`} loading="lazy" decoding="async"/>
                                                    </div>
                                                    <HoverOverlay img={img}/>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                    <LightboxOverlay/>
                </section>
            );
        }

        // ── Uniform / default grid layout ──
        const mdColsClass = mCols === 2 ? 'md:grid-cols-2' : mCols === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';
        const rows = [];
        for (let i = 0; i < items.length; i += mCols) {
            rows.push(items.slice(i, i + mCols));
        }
        const colTemplates3 = ['2fr 1fr 1fr', '1fr 1fr 2fr', '1fr 2fr 1fr'];
        const colTemplates4 = ['2fr 1fr 1fr 1fr', '1fr 1fr 1fr 2fr', '1fr 2fr 1fr 1fr'];
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="flex flex-col" style={{gap: gapPx}}>
                        {rows.map((row, ri) => {
                            const mosaicId = `mosaic-${ri}`;
                            const templates = mCols === 4 ? colTemplates4 : mCols === 3 ? colTemplates3 : null;
                            const colTemplate = templates ? (row.length >= mCols ? templates[ri % templates.length] : `repeat(${row.length}, 1fr)`) : null;
                            return (
                                <div key={ri}>
                                    {colTemplate && <style>{`@media(min-width:768px){#${mosaicId}{grid-template-columns:${colTemplate}}}`}</style>}
                                    <div id={mosaicId} className={`grid grid-cols-1 ${mdColsClass}`} style={{gap: gapPx}}>
                                        {row.map((img, i) => (
                                            <div key={img.id || i} className={`relative group overflow-hidden ${radiusClass} ${borderClass} cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                                style={{transitionDelay: visible ? `${(ri*mCols+i)*80}ms` : '0ms'}}
                                                onClick={() => setLightbox(ri * mCols + i)}>
                                                <div className="overflow-hidden" style={{aspectRatio: mAspect}}>
                                                    <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(mCols)} alt={img.title||''} className={`w-full h-full object-cover transition-transform duration-700 ${imgHoverClass}`} loading="lazy" decoding="async"/>
                                                </div>
                                                <HoverOverlay img={img}/>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <LightboxOverlay/>
            </section>
        );
    }

    // ── Slider + Thumbnails ──
    if (variant === 'thumbnails') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={sectionRef} className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    {/* Main image */}
                    <div className="relative rounded-2xl overflow-hidden shadow-xl mb-4 aspect-[16/9]" style={{ backgroundColor: '#111827' }}
                        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                        {items.map((img, i) => (
                            <div key={img.id || i} className={`absolute inset-0 transition-all duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
                                <img src={img.image_url} srcSet={buildSrcSet(img)} sizes="100vw" alt={img.title||''} className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(current)} loading="lazy" decoding="async"/>
                                {showTitle && img.title && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                                        <h3 className="text-xl font-bold text-white">{img.title}</h3>
                                        {showSub && img.subtitle && <p className="text-sm text-white/70 mt-1">{img.subtitle}</p>}
                                    </div>
                                )}
                            </div>
                        ))}
                        <Arrows/>
                    </div>
                    {/* Thumbnails */}
                    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                        {items.map((img, i) => (
                            <button key={img.id || i} onClick={() => setCurrent(i)}
                                className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === current ? 'ring-2' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                style={i === current ? { borderColor: s.accent, '--tw-ring-color': hexToRgba(s.accent, 0.3) } : undefined}>
                                <img src={img.image_url} alt="" className="w-full h-full object-cover" decoding="async"/>
                            </button>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <LightboxOverlay/>
                <style>{`.scrollbar-hide::-webkit-scrollbar{display:none}.scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}`}</style>
            </section>
        );
    }

    // ── Lightbox (grid that opens fullscreen on click) ──
    if (variant === 'lightbox') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {items.map((img, i) => (
                            <div key={img.id || i} onClick={() => setLightbox(i)}
                                className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{transitionDelay: visible ? `${i*60}ms` : '0ms'}}>
                                <div className="aspect-square overflow-hidden">
                                    <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(4)} alt={img.title||''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" decoding="async"/>
                                </div>
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all">
                                        <svg className="w-5 h-5" style={{ color: '#374151' }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/></svg>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <LightboxOverlay/>
            </section>
        );
    }

    // ── Polaroid ──
    if (variant === 'polaroid') {
        const rotations = [-3, 2, -1, 3, -2, 1, -3, 2];
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={sectionRef} className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                        {items.map((img, i) => (
                            <div key={img.id || i}
                                className={`rounded-sm shadow-lg p-3 pb-12 w-48 md:w-56 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:z-10 cursor-pointer ${visible ? 'opacity-100' : 'opacity-0'}`}
                                style={{backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', transform: `rotate(${rotations[i % rotations.length]}deg)`, transitionDelay: visible ? `${i*100}ms` : '0ms'}}
                                onClick={() => setLightbox(i)}>
                                <div className="aspect-square overflow-hidden">
                                    <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(3)} alt={img.title||''} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                                </div>
                                {showTitle && img.title && (
                                    <p className="absolute bottom-3 left-0 right-0 text-center text-sm" style={{ color: s.textSub }}>
                                        {img.title}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <LightboxOverlay/>
            </section>
        );
    }

    // ── Filmstrip ──
    if (variant === 'filmstrip') {
        const isPreview = !enableAnimations;
        return (
            <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={sectionRef} className={anim}>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8"><Header/></div>
                    {isPreview ? (
                        /* Preview del editor: grid compacto sin scroll */
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="grid grid-cols-3 gap-3">
                                {items.slice(0, 6).map((img, i) => (
                                    <div key={img.id || i} className="rounded-xl overflow-hidden shadow-sm">
                                        <div className="aspect-[3/2] overflow-hidden">
                                            <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(3)} alt={img.title||''} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                                        </div>
                                        {showTitle && img.title && <div className="p-2" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff' }}><p className="text-xs font-medium truncate" style={{color:s.text}}>{img.title}</p></div>}
                                    </div>
                                ))}
                            </div>
                            {items.length > 6 && <p className="text-center text-xs mt-3" style={{ color: s.textSub }}>+{items.length - 6} mas (scroll horizontal en el landing)</p>}
                        </div>
                    ) : (
                        /* Landing real: scroll horizontal con flechas + fade edges */
                        <div className="relative group/film">
                            {/* Fade edges */}
                            <div className="absolute left-0 top-0 bottom-12 w-20 bg-gradient-to-r z-10 pointer-events-none" style={{backgroundImage: `linear-gradient(to right, ${s.bg || '#f9fafb'}, transparent)`}}/>
                            <div className="absolute right-0 top-0 bottom-12 w-20 bg-gradient-to-l z-10 pointer-events-none" style={{backgroundImage: `linear-gradient(to left, ${s.bg || '#f9fafb'}, transparent)`}}/>
                            {/* Progressive blur for depth effect */}
                            <div className="absolute left-0 top-0 bottom-12 w-28 z-[9] pointer-events-none" style={{backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', maskImage: 'linear-gradient(to right, black 10%, transparent)', WebkitMaskImage: 'linear-gradient(to right, black 10%, transparent)'}}/>
                            <div className="absolute right-0 top-0 bottom-12 w-28 z-[9] pointer-events-none" style={{backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', maskImage: 'linear-gradient(to left, black 10%, transparent)', WebkitMaskImage: 'linear-gradient(to left, black 10%, transparent)'}}/>

                            {/* Arrow buttons */}
                            <button onClick={() => { const el = document.getElementById('filmstrip-scroll'); if (el) el.scrollBy({left: -440, behavior: 'smooth'}); }}
                                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all opacity-0 group-hover/film:opacity-100"
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.9)', color: s.dark ? '#ffffff' : '#4b5563' }}>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                            </button>
                            <button onClick={() => { const el = document.getElementById('filmstrip-scroll'); if (el) el.scrollBy({left: 440, behavior: 'smooth'}); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all opacity-0 group-hover/film:opacity-100"
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.9)', color: s.dark ? '#ffffff' : '#4b5563' }}>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                            </button>

                            <div className="flex gap-5 overflow-x-auto pb-6 px-8 snap-x snap-mandatory scrollbar-hide scroll-smooth" id="filmstrip-scroll">
                                {items.map((img, i) => (
                                    <div key={img.id || i} className="shrink-0 snap-center w-80 md:w-[420px] group rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-1"
                                        onClick={() => setLightbox(i)}>
                                        <div className="aspect-[16/10] overflow-hidden">
                                            <img src={img.image_url} srcSet={buildSrcSet(img)} sizes="(max-width:768px) 80vw, 420px" alt={img.title||''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async"/>
                                        </div>
                                        {showTitle && img.title && (
                                            <div className={`p-4 ${s.dark ? 'bg-white/5' : 'bg-white'}`}>
                                                <p className="text-base font-semibold" style={{color:s.text}}>{img.title}</p>
                                                {showSub && img.subtitle && <p className="text-sm mt-1" style={{color:s.textSub}}>{img.subtitle}</p>}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Scroll hint for mobile */}
                            <div className="flex justify-center items-center gap-2 mt-1 md:hidden">
                                <svg className="w-4 h-4 animate-bounce-x" style={{color: s.accent}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                                <span className="text-xs" style={{color: s.textSub}}>{content.swipe_hint || 'Desliza para ver mas'}</span>
                            </div>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <LightboxOverlay/>
                <style>{`
                    .scrollbar-hide::-webkit-scrollbar{display:none}
                    .scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}
                    @keyframes bounce-x{0%,100%{transform:translateX(0)}50%{transform:translateX(6px)}}
                    .animate-bounce-x{animation:bounce-x 1.5s ease-in-out infinite}
                `}</style>
            </section>
        );
    }

    // ── Coverflow 3D ──
    if (variant === 'coverflow') {
        const visibleCount = Math.min(items.length, 5);
        const half = Math.floor(visibleCount / 2);
        return (
            <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}
                onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    {/* Mobile: simple flat carousel */}
                    <div className="block sm:hidden relative">
                        <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                            {items.map((img, i) => (
                                <div key={img.id || i} className={`absolute inset-0 transition-opacity duration-500 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
                                    <img src={img.image_url} srcSet={buildSrcSet(img)} sizes="100vw" alt={img.title||''} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                                </div>
                            ))}
                            <Arrows/>
                        </div>
                        {showTitle && items[current]?.title && (
                            <p className="text-center mt-3 text-sm font-semibold" style={{color: s.text}}>{items[current].title}</p>
                        )}
                        <div className="flex justify-center gap-2 mt-3">
                            {items.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all ${i === current ? 'w-8' : 'w-2'}`} style={{backgroundColor: i === current ? s.accent : hexToRgba(s.accent, 0.3)}}/>)}
                        </div>
                    </div>
                    {/* Desktop: 3D coverflow */}
                    <div className="hidden sm:block">
                        <div className="relative flex items-center justify-center" style={{perspective: '1000px', minHeight: 400}}>
                            {items.map((img, i) => {
                                const offset = ((i - current + items.length) % items.length);
                                const normalizedOffset = offset > items.length / 2 ? offset - items.length : offset;
                                const isVisible = Math.abs(normalizedOffset) <= half;
                                if (!isVisible) return null;
                                const rotateY = normalizedOffset * 30;
                                const scale = normalizedOffset === 0 ? 1 : 0.85;
                                const translateX = normalizedOffset * 220;
                                const zIndex = visibleCount - Math.abs(normalizedOffset);
                                const opacity = normalizedOffset === 0 ? 1 : 0.7;
                                return (
                                    <div key={img.id || i}
                                        className="absolute transition-all duration-500 ease-out cursor-pointer rounded-xl overflow-hidden shadow-2xl"
                                        style={{
                                            transform: `translateX(${translateX}px) rotateY(${rotateY}deg) scale(${scale})`,
                                            zIndex,
                                            opacity,
                                            width: 380,
                                        }}
                                        onClick={() => normalizedOffset === 0 ? setLightbox(i) : setCurrent(i)}>
                                        <div className="aspect-[4/3] overflow-hidden">
                                            <img src={img.image_url} srcSet={buildSrcSet(img)} sizes="380px" alt={img.title||''} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                                        </div>
                                        {normalizedOffset === 0 && showTitle && img.title && (
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                                                <p className="text-white font-semibold">{img.title}</p>
                                                {showSub && img.subtitle && <p className="text-white/70 text-sm mt-1">{img.subtitle}</p>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex items-center justify-center gap-4 mt-6">
                            <button onClick={prev} className="w-10 h-10 rounded-full flex items-center justify-center transition-colors" style={{backgroundColor: hexToRgba(s.accent, 0.15), color: s.accent}}>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="flex gap-2">
                                {items.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all ${i === current ? 'w-8' : 'w-2'}`} style={{backgroundColor: i === current ? s.accent : hexToRgba(s.accent, 0.3)}}/>)}
                            </div>
                            <button onClick={next} className="w-10 h-10 rounded-full flex items-center justify-center transition-colors" style={{backgroundColor: hexToRgba(s.accent, 0.15), color: s.accent}}>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                            </button>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
                <LightboxOverlay/>
            </section>
        );
    }

    // ── Justified (Google Photos-style) ──
    if (variant === 'justified') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="flex flex-wrap" style={{gap: 4}}>
                        {items.map((img, i) => (
                            <div key={img.id || i}
                                className={`group overflow-hidden cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                style={{flexGrow: 1, minWidth: 200, maxHeight: 300, transitionDelay: visible ? `${i * 50}ms` : '0ms'}}
                                onClick={() => setLightbox(i)}>
                                <img src={img.image_url} srcSet={buildSrcSet(img)} sizes="(max-width:640px) 50vw, 33vw" alt={img.title||''}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    loading="lazy" decoding="async"/>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
                <LightboxOverlay/>
            </section>
        );
    }

    // ── Hover Reveal ──
    if (variant === 'hover-reveal') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <Header/>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {items.map((img, i) => (
                            <div key={img.id || i}
                                className={`group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{transitionDelay: visible ? `${i * 60}ms` : '0ms'}}
                                onClick={() => setLightbox(i)}>
                                <div className="aspect-video overflow-hidden">
                                    <img src={img.image_url} srcSet={buildSrcSet(img)} sizes={getImageSizes(3)} alt={img.title||''}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        loading="lazy" decoding="async"/>
                                </div>
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                                    <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                        {img.title && <h4 className="text-white font-bold text-base">{img.title}</h4>}
                                        {img.description && <p className="text-white/80 text-sm mt-1 line-clamp-2">{img.description}</p>}
                                        <span className="inline-block mt-2 text-sm font-medium" style={{color: s.accent}}>Ver mas →</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
                <LightboxOverlay/>
            </section>
        );
    }

    // ── Fullscreen ──
    if (variant === 'fullscreen') {
        return (
            <section className="relative overflow-hidden" style={{ backgroundColor: '#111827' }}
                onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
                onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                <div className="relative h-[500px] md:h-[700px]">
                    {items.map((img, i) => (
                        <div key={img.id || i} className={`absolute inset-0 transition-all duration-1000 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
                            <img src={img.image_url} srcSet={buildSrcSet(img)} sizes="100vw" alt={img.title||''} className="w-full h-full object-cover" decoding="async"/>
                            {showTitle && img.title && (
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8 md:p-12">
                                    <h3 className="text-2xl md:text-4xl font-bold text-white">{img.title}</h3>
                                    {showSub && img.subtitle && <p className="text-lg text-white/70 mt-2">{img.subtitle}</p>}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <Arrows/>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                    {config.pagination_style === 'ring' ? (
                        <RingProgress/>
                    ) : (
                        <div className="flex gap-2">
                            {items.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all ${i === current ? 'w-8 bg-white' : 'w-2 bg-white/40'}`}/>)}
                        </div>
                    )}
                </div>
            </section>
        );
    }

    // ── Carousel (default) ──
    return (
        <section className="relative overflow-hidden" style={{ backgroundColor: '#111827' }}
            onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
            onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <div className="relative h-[350px] md:h-[500px]">
                {items.map((slide, i) => (
                    <div key={slide.id || i} className={`absolute inset-0 transition-all duration-700 ease-in-out ${i === current ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}>
                        <img src={slide.image_url} srcSet={buildSrcSet(slide)} sizes="100vw" alt={slide.title||''} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                        {showTitle && (slide.title || slide.subtitle) && (
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end">
                                <div className="p-8 md:p-12 text-white max-w-3xl">
                                    {slide.title && <h3 className="text-2xl md:text-3xl font-bold mb-2">{slide.title}</h3>}
                                    {showSub && slide.subtitle && <p className="text-base md:text-lg text-white/80">{slide.subtitle}</p>}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {items.length > 1 && (
                <>
                    <Arrows/>
                    <div className="absolute bottom-0 left-0 right-0">
                        {config.pagination_style === 'ring' ? (
                            <RingProgress/>
                        ) : (
                            <>
                                <div className="h-1 bg-white/20"><div className="h-full bg-white/70 transition-all duration-500" style={{width:`${((current+1)/items.length)*100}%`}}/></div>
                                <Dots/>
                            </>
                        )}
                    </div>
                </>
            )}
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
        </section>
    );
}
